import { Component } from '../Component';
import { ArgSpecDictionary } from '../ComponentUtils';
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules'; // Import Pagination module
import { SwiperOptions } from 'swiper/types';

import swiperStyles from '../../../node_modules/swiper/swiper-bundle.min.css?inline';
import { CSSBreakpoint } from '../../common/constants-theme';
import gsap from 'gsap';
import { rosAnimation, STAnimationType } from '../../utils/ScrollTriggerHelper';

const DEBUG_VERBOSE = false;
export const HHDSAmenitiesTagName: string = 'hhds-amenities';
const TAG_NAME: string = HHDSAmenitiesTagName;
const LOG_PREFIX: string = `[${TAG_NAME}]`;

export const HHDSAmenitiesAttrNames = {
	rosAnimation: 'ros-animation',
	LegendRows: 'legend-rows',
	LegendCols: 'legend-cols',
};

const Attrs = HHDSAmenitiesAttrNames;

Swiper.use([Navigation]);

enum Diameter {
	SM = 16,
	LG = 25,
}

export class HHDSAmenities extends Component {
	private imageContainer!: HTMLElement;
	private svg!: SVGSVGElement;
	private image!: HTMLImageElement;

	private scale!: number;

	private legendItems!: HTMLElement[];
	private swiper!: Swiper;
	private pinsContainer!: HTMLElement;
	private markerWidth!: number;
	private markerHeight!: number;
	private pins: HTMLElement[] = [];

	private onScrollTween: gsap.core.Tween | null = null;

	// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

	constructor() {
		super();
	}

	get swiperOptions() {
		const cols = this.vars.get<number>(Attrs.LegendCols);

		console.log({ cols });

		const swiperOptions: SwiperOptions = {
			loop: false,
			speed: 750,
			slidesPerView: 1,
			slidesPerGroup: 1,
			spaceBetween: 20, // Based on 1.25 rem × 16px

			navigation: {
				nextEl: this.querySelector('.swiper-button-next') as HTMLElement,
				prevEl: this.querySelector('.swiper-button-prev') as HTMLElement,
			},

			breakpoints: {
				[CSSBreakpoint.MD]: {
					slidesPerView: 2,
					slidesPerGroup: 2, // slide two at a time
				},

				[CSSBreakpoint.LG]: {
					slidesPerView: cols,
					slidesPerGroup: 1,
					spaceBetween: 24, // Based on 1.5 rem × 16px
				},
			},
		};

		return swiperOptions;
	}

	protected override init(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'init');

		const html: string = /* html */ `
			<div class="${TAG_NAME}">
				<slot></slot>
			</div>
    `;

		this.setInnerHtml('', '', html, [swiperStyles]);

		this.observeSlotChanges(true);

		// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

		this.initDOMElements();

		this.initLegendItems();

		this.initSwiper();
	}

	protected override destroy(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'destroy');

		this.swiper.destroy();
		this.onScrollTween?.kill();
	}

	override onAttributeChanged(name: string, _oldValue: string, _newValue: string): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onAttributeChanged: ${name}`);
		this.reinit();
	}

	override onSlotChange(_slot: HTMLSlotElement, _elements: HTMLElement[]): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onSlotChange`);
	}

	static override argSpecs(): ArgSpecDictionary {
		return ArgSpecs;
	}

	// ////////////////////////////////////////////////////////////////////

	initDOMElements() {
		// Get a handle to all the elemnts we need.

		this.imageContainer = this.querySelector(`.${TAG_NAME}__image`) as HTMLElement;
		this.image = this.imageContainer.querySelector('img') as HTMLImageElement;
		this.svg = this.imageContainer.querySelector('svg') as SVGSVGElement;
		this.pinsContainer = this.querySelector(`.${TAG_NAME}__image-pins`) as HTMLElement;

		// Wait for the image to load before calculating scale
		if (this.image.complete) {
			DEBUG_VERBOSE && console.log(this.initDOMElements.name, 'Image was loaded before listener applied');
			this.setInitialScale();
		} else {
			DEBUG_VERBOSE && console.log(this.initDOMElements.name, 'Waiting for image to load');
			this.image.onload = () => {
				DEBUG_VERBOSE && console.log(this.initDOMElements.name, 'Image is loaded');
				this.setInitialScale();
			};
		}

		this.legendItems = Array.from(this.querySelectorAll('[data-location]')) as HTMLElement[];

		DEBUG_VERBOSE && console.log(this.initDOMElements.name, `this.legendItems`, this.legendItems);
	}

	// ////////////////////////////////////////////////////////////////////

	calculateScale() {
		DEBUG_VERBOSE && logger.log(this.calculateScale.name);

		return this.clientWidth / this.svgDims.width;
	}

	// ////////////////////////////////////////////////////////////////////

	setInitialScale() {
		this.scale = this.calculateScale();

		DEBUG_VERBOSE &&
			console.log(
				`[Dollhouse] The dollhouse image is ${this.image.naturalWidth}x${this.image.naturalHeight}, scaled to ${this.image.clientWidth}x${this.image.clientHeight}`
			);
		DEBUG_VERBOSE && console.log(`[Dollhouse] Initial image scale is ${this.scale}`);

		this.createPins();

		this.initPinsAnimation();
	}

	// ////////////////////////////////////////////////////////////////////

	private initPinsAnimation() {
		DEBUG_VERBOSE && logger.log('initPinsAnimation', this.pins);

		if (this.vars.get<boolean>(Attrs.rosAnimation)) {
			this.pins.forEach((pin) => {
				gsap.set(pin, { opacity: 0 });
			});

			this.onScrollTween?.kill();

			requestAnimationFrame(() => {
				this.onScrollTween = rosAnimation(this, STAnimationType.triggered, {
					start: '30%',
					end: '100%',
					onStart: () => {
						this.pins.forEach((pin, index) => {
							gsap.to(pin, {
								opacity: 1,
								duration: 0.8,
								delay: index * 0.1,
								ease: 'power2.out',
							});
						});
					},
				});
			});
		}
	}

	// ////////////////////////////////////////////////////////////////////

	get svgDims() {
		return { width: this.svg.width.baseVal.value, height: this.svg.height.baseVal.value };
	}

	// ////////////////////////////////////////////////////////////////////

	createPins() {
		// Get the annotations from the image and re-create them in the DOM layer

		// First, set the SVG container to be the natural size of the image
		this.pinsContainer.style.width = this.svgDims.width + 'px';
		this.pinsContainer.style.height = this.svgDims.height + 'px';

		let markers = [...this.svg.querySelectorAll('g[class^="location-"]')] as SVGGElement[];

		// Ensure there is no child elements - there was an issue in one SVG with a location-1 inside another location-1
		// This resulted in two pins being generated on the same coordinates
		markers = Array.from(markers).filter((el) => {
			return !el.closest('g[class^="location-"] g[class^="location-"]');
		});

		// Ensure they are in numerical order so @a11y can tab through in the correct order
		markers.sort((a, b) => {
			const aClass = a.classList[0] || '';
			const bClass = b.classList[0] || '';
			return aClass.localeCompare(bClass, undefined, { numeric: true });
		});

		markers.forEach((marker: SVGElement) => {
			// Get the id of the annotation from the SVG class

			const id = marker.classList[0].replace('location-', '');

			// Get the position of the marker in the SVG, offseting it by the position of the image on the page
			const markerRect = marker.getBoundingClientRect();

			const markerX = markerRect.left - this.image.getBoundingClientRect().left;
			const markerY = markerRect.top - this.image.getBoundingClientRect().top;

			// Set the width and height to be identical to prevent any rounding errors
			this.markerWidth = this.markerHeight = markerRect.width;

			// Create the pin
			this.createPin(markerX, markerY, id);
		});

		// Scale the pins to now match the image
		this.scalePins();
		this.scalePinText();

		// Add a listener so that we resize the pins on window resizing
		window.addEventListener('resize', () => {
			this.scalePins();
			this.scalePinText();
		});
	}

	createPin(x: number, y: number, id: string) {
		const pin = document.createElement('button');
		pin.type = 'button';

		pin.classList.add(`${TAG_NAME}__pin`);
		pin.classList.add('location-' + id);
		pin.style.top = y + 'px';
		pin.style.left = x + 'px';
		// pin.tabIndex = 0;

		// Set the pin to be the same width and height as the SVG marker
		pin.style.width = `${this.markerWidth}px`;
		pin.style.height = `${this.markerHeight}px`;

		const label = document.createElement('p');
		label.innerHTML = id;

		pin.appendChild(label);

		pin.addEventListener('click', () => {
			this.setActivePin(id);
		});

		this.pins.push(pin);
		this.pinsContainer.appendChild(pin);
	}

	// ////////////////////////////////////////////////////////////////////

	scalePins() {
		let scale = this.calculateScale();

		let scaledDiameter = this.pins[0].clientWidth * scale;

		this.pinsContainer.style.transform = `scale(${scale})`;

		let targetDiameter: number | null = null;
		let pinScale = 1;

		if (window.innerWidth < CSSBreakpoint.MD) {
			targetDiameter = Diameter.SM;
			pinScale = targetDiameter / scaledDiameter;
		} else if (window.innerWidth >= CSSBreakpoint.MD && window.innerWidth < CSSBreakpoint.LG) {
			targetDiameter = Diameter.LG;
			pinScale = targetDiameter / scaledDiameter;
		}

		if (targetDiameter) {
			DEBUG_VERBOSE &&
				console.log(`[Dollhouse] Pins scaled up by the container are currently ${this.pins[0].getBoundingClientRect().width}`);
			DEBUG_VERBOSE && console.log(`[Dollhouse] Need to scale pins by a factor of ${pinScale} to maintain a size of ${targetDiameter}`);

			DEBUG_VERBOSE && console.log(`[Dollhouse] Pins are now ${scaledDiameter} scaled to ${this.pins[0].getBoundingClientRect().width}`);
		} else {
			DEBUG_VERBOSE && console.log(`[Dollhouse] No scaling needed. Pins are currently ${this.pins[0].getBoundingClientRect().width}`);
		}

		this.pinsContainer.style.transform = `scale(${scale})`;
		this.pinsContainer.style.setProperty('--pin-scale', `${pinScale}`);
	}

	scalePinText() {
		const scale = this.calculateScale();

		let textScale = 1; // Default for desktop

		if (window.matchMedia(`(max-width: ${CSSBreakpoint.MD - 1}px)`).matches) {
			//Text is 10px at this breakpoint
			const textSizeOriginal = 10;
			const textSizeTarget = 9;

			textScale = this.calculateTextScale(scale, textSizeOriginal, textSizeTarget);

			DEBUG_VERBOSE && logger.log('scalePinText', '< sm', textScale);
		} else if (window.matchMedia(`(max-width: ${CSSBreakpoint.LG - 1}px)`).matches) {
			// Text is 10px at this breakpoint
			const textSizeOriginal = 10;
			const textSizeTarget = 13;

			textScale = this.calculateTextScale(scale, textSizeOriginal, textSizeTarget);

			DEBUG_VERBOSE && logger.log('scalePinText', 'md', textScale);
		}

		DEBUG_VERBOSE && logger.log('scalePinText', { scale, textScale });

		this.pinsContainer.style.setProperty('--pin-letter-scale', `${textScale}`);
	}

	calculateTextScale(scale: number, textSizeOriginal: number, textSizeTarget: number) {
		const currentTextScale = textSizeOriginal * scale;

		return textSizeTarget / currentTextScale;
	}

	setActivePin(idStr: string) {
		DEBUG_VERBOSE && logger.log(this.setActivePin.name, idStr);

		this.legendItems.forEach((legendItem) => {
			const { location } = legendItem.dataset;
			legendItem.classList.toggle('active', location === idStr);
		});

		// Scroll the legend to the right place

		const legendRows = this.vars.get<number>(Attrs.LegendRows);

		console.log(legendRows);

		const foo = (parseInt(idStr, 10) - 1) / legendRows;

		const slideIndex = Math.floor(foo);

		DEBUG_VERBOSE && logger.log(this.setActivePin.name, { idStr, foo, slideIndex });

		this.swiper.slideTo(slideIndex);

		this.pins.forEach((pin) => {
			const className = `location-${idStr}`;
			pin.classList.toggle('active', pin.classList.contains(className));
		});
	}

	// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

	initLegendItems() {
		this.legendItems.forEach((legendItem) => {
			legendItem.addEventListener('click', (event: MouseEvent) => {
				event.preventDefault();

				const { location: idStr } = legendItem.dataset;

				DEBUG_VERBOSE && logger.log(this.initLegendItems.name, idStr);

				if (idStr) {
					this.setActivePin(idStr);
				} else {
					console.warn('No idStr set for element: ', legendItem);
				}
			});

			const onMouseEnter = () => {
				legendItem.removeEventListener('mouseenter', onMouseEnter);

				const labelEl = legendItem.querySelector('.hhds-amenities__label') as HTMLElement;

				if (labelEl) {
					// Restore duration after having it initially set to 0 to avoid initial draw in
					labelEl.style.setProperty('--anim-dur', 'calc(var(--underline-duration) / 2)');
				}
			};
			legendItem.addEventListener('mouseenter', onMouseEnter);
		});
	}

	initSwiper() {
		const swiperEl = this.querySelector('.swiper') as HTMLElement;

		this.swiper = new Swiper(swiperEl, this.swiperOptions);
	}
}

export const ArgSpecs: ArgSpecDictionary = {
	[Attrs.rosAnimation]: {
		description: 'Enable scroll-triggered animations.',
		defaultValue: true,
		type: Boolean,
	},
	[Attrs.LegendRows]: {
		description: 'How many rows should be in each column of the legend.',
		defaultValue: 4,
		type: Number,
	},
	[Attrs.LegendCols]: {
		description:
			'How many rows should be in each column of the legend. Normally calculated by dividing the total number of legend items by the legend rows value.',
		defaultValue: 4,
		type: Number,
	},
};
